<template>
  <div v-if="item" class="page-shell news-detail-page">
    <section class="section">
      <div class="container news-detail-layout">
        <article class="news-detail-main">
          <button type="button" class="news-back-button" @click="goBack">
            <span aria-hidden="true">&#8592;</span>
            {{ backText }}
          </button>

          <header class="news-detail-header" v-reveal>
            <span class="news-detail-tag">{{ $pick(categoryLabel(item.category)) }}</span>
            <h1>{{ $pick(item.title) }}</h1>
            <div class="news-detail-meta">
              <span>{{ publishDateLabel }}{{ item.date }}</span>
              <span>{{ sourceLabel }}{{ $pick(item.sourceName) }}</span>
              <span>{{ viewsLabel }}{{ item.views }}</span>
            </div>
          </header>

          <div class="news-detail-body" v-reveal>
            <p v-for="paragraph in $pick(item.content)" :key="paragraph">{{ paragraph }}</p>

            <img
              v-for="image in item.images || []"
              :key="image"
              class="news-detail-image"
              :src="image"
              :alt="$pick(item.title)"
            />
          </div>
        </article>

        <aside class="news-detail-side">
          <section class="news-side-box" v-reveal>
            <h3>{{ relatedTitle }}</h3>
            <router-link
              v-for="news in related"
              :key="news.slug"
              class="news-side-link"
              :to="`/news/detail/${news.slug}`"
            >
              <strong>{{ $pick(news.title) }}</strong>
              <span>{{ publishDateLabel }}{{ news.date }} · {{ viewsLabel }}{{ news.views }}</span>
            </router-link>
          </section>

          <section class="news-side-box" v-reveal>
            <h3>{{ latestTitle }}</h3>
            <router-link
              v-for="news in latest"
              :key="news.slug"
              class="news-side-link"
              :to="`/news/detail/${news.slug}`"
            >
              <strong>{{ $pick(news.title) }}</strong>
              <span>{{ publishDateLabel }}{{ news.date }} · {{ viewsLabel }}{{ news.views }}</span>
            </router-link>
          </section>
        </aside>
      </div>
    </section>
  </div>
</template>

<script>
import { getLatestNews, getNewsBySlug, getRelatedNews } from "../../data/news";

export default {
  name: "NewsDetailPage",
  computed: {
    backText() {
      return this.locale === "zh-CN" ? "返回" : "Back";
    },
    publishDateLabel() {
      return this.locale === "zh-CN" ? "发表日期：" : "Date: ";
    },
    sourceLabel() {
      return this.locale === "zh-CN" ? "来源：" : "Source: ";
    },
    viewsLabel() {
      return this.locale === "zh-CN" ? "浏览次数：" : "Views: ";
    },
    relatedTitle() {
      return this.locale === "zh-CN" ? "相关新闻" : "Related News";
    },
    latestTitle() {
      return this.locale === "zh-CN" ? "最新新闻" : "Latest News";
    },
    item() {
      return getNewsBySlug(this.$route.params.slug);
    },
    related() {
      return getRelatedNews(this.item, 4);
    },
    latest() {
      return getLatestNews(4).filter((entry) => !this.item || entry.slug !== this.item.slug);
    }
  },
  methods: {
    categoryLabel(category) {
      return category === "industry"
        ? { "zh-CN": "行业资讯", "en-US": "Industry News" }
        : { "zh-CN": "本站动态", "en-US": "Company News" };
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }

      this.$router.push("/news/company");
    }
  }
};
</script>

<style scoped>
.news-detail-page {
  padding-top: 132px;
}

.news-detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 48px;
  align-items: start;
}

.news-back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  padding: 10px 18px;
  border: 1px solid rgba(0, 82, 155, 0.16);
  border-radius: 999px;
  background: rgba(0, 82, 155, 0.06);
  color: var(--brand-blue);
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.25s ease, background-color 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
}

.news-back-button:hover {
  border-color: var(--brand-blue);
  background: var(--brand-blue);
  color: #ffffff;
  transform: translateY(-2px);
}

.news-detail-header h1 {
  margin: 14px 0 18px;
  color: #242b35;
  font-size: 38px;
  line-height: 1.35;
}

.news-detail-tag {
  display: inline-flex;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(239, 43, 45, 0.08);
  color: #ef2b2d;
  font-size: 13px;
  font-weight: 700;
}

.news-detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  color: #a3aab5;
  font-size: 14px;
}

.news-detail-body {
  margin-top: 32px;
}

.news-detail-body p {
  margin: 0 0 24px;
  color: #465365;
  font-size: 18px;
  line-height: 2;
}

.news-detail-image {
  width: 100%;
  margin: 28px 0;
  border-radius: 12px;
}

.news-detail-source {
  margin-top: 28px;
}

.news-detail-source a {
  color: var(--brand-blue);
  font-weight: 600;
}

.news-detail-side {
  position: sticky;
  top: 132px;
}

.news-side-box {
  margin-bottom: 24px;
  padding: 24px;
  border: 1px solid var(--line);
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 16px 40px rgba(18, 54, 110, 0.08);
}

.news-side-box h3 {
  margin: 0 0 18px;
  color: #202a39;
  font-size: 24px;
}

.news-side-link {
  display: block;
  padding: 14px 0;
  border-bottom: 1px solid #eef1f5;
}

.news-side-link:last-child {
  border-bottom: 0;
}

.news-side-link strong {
  display: block;
  margin-bottom: 8px;
  color: #293446;
  line-height: 1.6;
}

.news-side-link span {
  color: #a4acb8;
  font-size: 13px;
}

@media (max-width: 960px) {
  .news-detail-page {
    padding-top: 82px;
  }

  .news-detail-layout {
    grid-template-columns: 1fr;
  }

  .news-detail-side {
    position: static;
  }

  .news-detail-header h1 {
    font-size: 30px;
  }

  .news-detail-body p {
    font-size: 16px;
  }
}

@media (max-width: 520px) {
  .news-detail-layout {
    gap: 30px;
  }

  .news-detail-header h1 {
    font-size: 24px;
    line-height: 1.4;
  }

  .news-detail-meta {
    display: grid;
    gap: 8px;
    font-size: 13px;
  }

  .news-detail-body {
    margin-top: 24px;
  }

  .news-detail-body p {
    font-size: 15px;
    line-height: 1.85;
  }

  .news-side-box {
    padding: 20px 18px;
    border-radius: 18px;
  }
}
</style>
